// The one-time "what's new" popup on the tool page (components/AnnouncementDialog.tsx).
//
// Each browser sees an announcement once: closing it stores its id in localStorage. To announce the next
// release, give it a NEW id (so everyone sees it again) and new text. To switch the popup off, set
// ANNOUNCEMENT to null. After `showUntil` it stops appearing by itself, so a forgotten one goes stale quietly.

export type Announcement = {
    id: string;
    title: string;
    paragraphs: string[];
    linkLabel: string;
    linkUrl: string;
    showUntil: string; // last day it is shown, YYYY-MM-DD
};

export const ANNOUNCEMENT: Announcement | null = {
    id: "release-4.2.2",
    title: "JIM 4.2.2 is live",
    paragraphs: [
        "The results layout of JIM 4.2 is back: sector-exposure lines are reported as Finance Enabling again and investees appear as their own rows. The release also fixes the inflation adjustment of estimated jobs: in JIM 4.2 estimated jobs were understated because of a mistaken inflation adjustment and now come out higher. Scope 3 downstream emissions are set to zero where they do not belong. Some results therefore differ from earlier runs.",
        "If you need the previous version for a while, JIM 4.2 stays available at https://v42.jointimpactmodel.org with your usual login, for a transition period.",
    ],
    linkLabel: "Read the release notes",
    linkUrl: "https://www.jointimpactmodel.org/release-notes",
    showUntil: "2027-03-31",
};

const storageKey = (a: Announcement) => `jim.announcement.seen.${a.id}`;

// Storage can be unavailable (private mode, blocked site data): then the popup still shows, but cannot be
// remembered -- better than throwing on the tool page.
export const isAnnouncementSeen = (a: Announcement): boolean => {
    try {
        return localStorage.getItem(storageKey(a)) === "1";
    } catch {
        return false;
    }
};

export const markAnnouncementSeen = (a: Announcement): void => {
    try {
        localStorage.setItem(storageKey(a), "1");
    } catch {
        // nothing to do: it will simply show again next time
    }
};

export const shouldShowAnnouncement = (a: Announcement | null, now: Date = new Date()): a is Announcement =>
    a !== null && now <= new Date(`${a.showUntil}T23:59:59`) && !isAnnouncementSeen(a);
